import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { Search } from 'lucide-react'
import { apiFetch, qs } from '../lib/api'
import { useDebounced } from '../lib/hooks'
import type { Paged, UserRow } from '../lib/types'
import { DataTable } from '../components/data-table'
import { Pagination } from '../components/pagination'
import { PageHeader, formatDate } from '../components/page'
import { ErrorState, Spinner } from '../components/states'
import { Badge } from '../components/badge'

const columns: ColumnDef<UserRow, any>[] = [
	{
		header: 'Email',
		accessorKey: 'email',
		cell: ({ row }) => (
			<div className="min-w-0">
				<div className="flex items-center gap-2">
					<span className="truncate font-medium">{row.original.erasedAt ? 'Deleted user' : row.original.email}</span>
					{row.original.erasedAt ? (
						<Badge tone="neutral">deleted</Badge>
					) : row.original.deactivatedAt ? (
						<Badge tone="warning">deactivated</Badge>
					) : null}
				</div>
				{row.original.name && <div className="truncate text-xs text-ink-soft">{row.original.name}</div>}
			</div>
		),
	},
	{ header: 'Company', accessorKey: 'company', cell: ({ getValue }) => getValue() || '—' },
	{
		header: 'Instances',
		accessorKey: 'instanceCount',
		cell: ({ getValue }) => <span className="tabular-nums">{getValue<number>()}</span>,
	},
	{
		header: 'Source',
		accessorKey: 'source',
		cell: ({ getValue }) => <Badge tone={getValue() === 'whmcs' ? 'warning' : 'neutral'}>{getValue<string>()}</Badge>,
	},
	{
		header: '2FA',
		accessorKey: 'twoFactorEnabled',
		cell: ({ getValue }) => (getValue() ? <Badge tone="success">on</Badge> : <span className="text-ink-faint">off</span>),
	},
	{ header: 'Created', accessorKey: 'createdAt', cell: ({ getValue }) => formatDate(getValue<string>()) },
]

export function UsersPage() {
	const navigate = useNavigate()
	const [search, setSearch] = useState('')
	const [page, setPage] = useState(1)
	const q = useDebounced(search)

	const { data, isPending, error, refetch } = useQuery({
		queryKey: ['users', q, page],
		queryFn: () => apiFetch<Paged<UserRow>>(`/api/admin/users${qs({ q, page })}`),
	})

	return (
		<>
			<PageHeader title="Users" subtitle="Customer accounts, their instances and sessions." />

			<div className="card">
				<div className="border-b border-line p-3">
					<div className="relative max-w-sm">
						<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint" />
						<input
							className="input pl-9"
							placeholder="Search email, name, company, Chargebee or WHMCS id"
							value={search}
							onChange={(e) => {
								setSearch(e.target.value)
								// A new search invalidates the current page number.
								setPage(1)
							}}
						/>
					</div>
				</div>

				{isPending ? (
					<Spinner />
				) : error ? (
					<ErrorState error={error} onRetry={refetch} />
				) : (
					<>
						<DataTable
							columns={columns}
							rows={data.rows}
							emptyMessage={q ? `No users match “${q}”.` : 'No users yet.'}
							onRowClick={(row) => navigate(`/users/${row.id}`)}
						/>
						<Pagination page={data.page} limit={data.limit} total={data.total} onPageChange={setPage} />
					</>
				)}
			</div>
		</>
	)
}
